/**
 * Video+Audio Processing Pipeline for The Clip Looper
 *
 * IMPORTANT: This file uses ffmpeg.wasm's JavaScript API (FFmpeg.exec())
 * which runs FFmpeg commands inside the WebAssembly sandbox in the browser.
 * This is NOT Node.js child_process - all processing happens client-side.
 */

import { fetchFile } from '@ffmpeg/util';
import { buildCombinedFilterComplex, calculateExpectedDuration } from './filters.js';
import { generateOutputFilename } from '../utils/format.js';

/**
 * Process a video with crossfade looping
 *
 * @param {FFmpeg} ffmpeg - The loaded FFmpeg instance
 * @param {object} options - Processing options
 * @param {File} options.file - The input video file
 * @param {number} options.duration - Video duration in seconds
 * @param {boolean} options.hasAudio - Whether the video has audio
 * @param {number} options.repeatCount - Number of repetitions (2-10)
 * @param {number} options.crossfadeDuration - Crossfade duration in seconds
 * @param {boolean} options.useAcrossfade - Whether to use acrossfade (true) or afade fallback
 * @param {function} options.onProgress - Progress callback (0-100)
 * @returns {Promise<{blobUrl: string, fileSize: number, duration: number, filename: string}>}
 */
export async function processVideo(ffmpeg, options) {
  const {
    file,
    duration,
    hasAudio,
    repeatCount,
    crossfadeDuration,
    useAcrossfade = true,
    onProgress = null
  } = options;

  const inputFilename = 'input.mp4';
  const outputFilename = 'output.mp4';
  const expectedDuration = calculateExpectedDuration(duration, repeatCount, crossfadeDuration);

  // Set up progress handler using time-based calculation
  let progressHandler = null;
  if (onProgress) {
    progressHandler = ({ time }) => {
      if (time > 0 && expectedDuration > 0) {
        const currentTime = time / 1000000; // Convert microseconds to seconds
        const pct = Math.min((currentTime / expectedDuration) * 100, 99);
        onProgress(pct);
      }
    };
    ffmpeg.on('progress', progressHandler);
  }

  try {
    // Write input file to virtual FS
    await ffmpeg.writeFile(inputFilename, await fetchFile(file));

    let exitCode;

    // Handle single repeat (just copy)
    if (repeatCount === 1) {
      // Run via ffmpeg.wasm WASM sandbox API (browser-side, not shell)
      exitCode = await ffmpeg.exec([
        '-i', inputFilename,
        '-c', 'copy',
        outputFilename
      ]);
    } else {
      // Build input arguments - reference same file multiple times
      const inputArgs = [];
      for (let i = 0; i < repeatCount; i++) {
        inputArgs.push('-i', inputFilename);
      }

      // Build filter complex for video+audio crossfade
      const filterComplex = buildCombinedFilterComplex(
        repeatCount,
        duration,
        crossfadeDuration,
        hasAudio,
        useAcrossfade
      );

      // Build output arguments
      const outputArgs = [
        '-map', '[vout]',
        '-c:v', 'libx264',
        '-preset', 'fast',
        '-crf', '23'
      ];

      if (hasAudio) {
        outputArgs.push('-map', '[aout]');
        outputArgs.push('-c:a', 'aac', '-b:a', '128k');
      }

      // Run via ffmpeg.wasm WASM sandbox API (browser-side, not shell)
      exitCode = await ffmpeg.exec([
        ...inputArgs,
        '-filter_complex', filterComplex,
        ...outputArgs,
        outputFilename
      ]);
    }

    if (exitCode !== 0) {
      throw new Error(`FFmpeg exited with code ${exitCode}`);
    }

    // Report 100% when actually complete
    if (onProgress) {
      onProgress(100);
    }

    // Read output file
    const outputData = await ffmpeg.readFile(outputFilename);

    // Create blob URL
    const blob = new Blob([outputData.buffer], { type: 'video/mp4' });
    const blobUrl = URL.createObjectURL(blob);

    // Generate descriptive filename
    const filename = generateOutputFilename(repeatCount, crossfadeDuration);

    // Clean up virtual FS
    await cleanupFiles(ffmpeg, [inputFilename, outputFilename]);

    return {
      blobUrl,
      fileSize: blob.size,
      duration: expectedDuration,
      filename
    };

  } catch (error) {
    // Clean up on error
    await cleanupFiles(ffmpeg, [inputFilename, outputFilename]);
    throw error;
  } finally {
    // Remove progress handler
    if (progressHandler) {
      ffmpeg.off('progress', progressHandler);
    }
  }
}

/**
 * Clean up files from virtual filesystem
 *
 * @param {FFmpeg} ffmpeg - The FFmpeg instance
 * @param {string[]} filenames - Files to delete
 */
async function cleanupFiles(ffmpeg, filenames) {
  for (const filename of filenames) {
    try {
      await ffmpeg.deleteFile(filename);
    } catch {
      // Ignore cleanup errors
    }
  }
}

/**
 * Revoke a blob URL to free memory
 *
 * @param {string|null} blobUrl - The blob URL to revoke
 */
export function revokeBlobUrl(blobUrl) {
  if (blobUrl) {
    try {
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Ignore revocation errors
    }
  }
}

/**
 * Clean up previous output before re-processing
 *
 * @param {FFmpeg} ffmpeg - The FFmpeg instance
 * @param {object} previousOutput - Previous output object with blobUrl
 */
export async function cleanupPreviousOutput(ffmpeg, previousOutput) {
  // Revoke previous blob URL
  if (previousOutput?.blobUrl) {
    revokeBlobUrl(previousOutput.blobUrl);
  }

  // Clean up any lingering files in virtual FS
  await cleanupFiles(ffmpeg, ['input.mp4', 'output.mp4', 'probe_input.mp4']);
}
